import { useState, useCallback, useEffect } from 'react';
import { Check, FlaskConical, Droplets, Beaker as BeakerIcon } from 'lucide-react';
import { useExperiment } from '@/context';
import { Burette } from '@/components/lab';
import ErlenmeyerFlask from '@/components/lab/ErlenmeyerFlask';
import MagneticStirrer from '@/components/lab/MagneticStirrer';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Obtaining and Diluting Water Sample',
    content: 'Obtain an unknown water sample for calcium analysis and record the source. Dilute the sample 5-fold using DI water. Use a clean, rinsed volumetric pipette to transfer exactly 25.00 mL of the diluted water sample into each of 3 labelled Erlenmeyer flasks. Use a rubber safety bulb - do not pipet by mouth.'
  },
  2: {
    title: 'Buffer, Mg/EDTA, and Indicator',
    content: 'Add 2 mL of ammonia/ammonium chloride buffer first to establish pH 10 (at exhaust hood - ammonia is a respiratory irritant). Then add 1 mL of Mg/EDTA solution (promotes sharper colour change at endpoint). Finally add 5-6 drops of EBT indicator. The solution should turn red if Ca2+ and/or Mg2+ are present. If the solution is blue initially, the water has very low hardness.'
  },
  3: {
    title: 'Refilling the Burette',
    content: 'Ensure the burette is filled with standardized EDTA solution. The molarity determined in Part II will be used to calculate hardness. Check for air bubbles and ensure the stopcock operates smoothly.'
  },
  4: {
    title: 'Titrating the Sample',
    content: 'Titrate the water sample the same way as the standard. The EDTA binds Ca2+ and Mg2+ ions, releasing them from the indicator complex. At the endpoint, the red/purple colour disappears and a distinct blue colour appears. Record initial and final buret volumes to the nearest 0.02 mL.'
  },
  5: {
    title: 'Recording the Volume',
    content: 'Record the final burette reading when the distinct blue endpoint is reached. This volume, combined with the EDTA molarity, will be used to calculate the calcium content (ppm) of the water sample. Remember that the sample was diluted 5-fold, so account for this in the final result.'
  }
};

const EBT_WINE_RED = '#8B2252';
const EBT_BLUE = '#3B82F6';

function interpolateColor(color1: string, color2: string, factor: number): string {
  const hex = (c: string) => parseInt(c, 16);
  const r1 = hex(color1.slice(1, 3));
  const g1 = hex(color1.slice(3, 5));
  const b1 = hex(color1.slice(5, 7));
  const r2 = hex(color2.slice(1, 3));
  const g2 = hex(color2.slice(3, 5));
  const b2 = hex(color2.slice(5, 7));

  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export default function Part3SampleAnalysis() {
  const { state, updatePartData, completeStep, isStepCompleted } = useExperiment();
  const partState = state?.experimentData?.sampleAnalysis || {
    sampleAdded: false,
    bufferAdded: false,
    indicatorAdded: false,
    flaskColor: '#FFFFFF',
    buretteVolume: 50,
    stopcockOpen: false,
    sampleResult: {
      volumeEDTA: null,
      ppmCalcium: null
    }
  };

  const [stirrerOn, setStirrerOn] = useState(false);
  const [titrating, setTitrating] = useState(false);
  const [titrationProgress, setTitrationProgress] = useState(0);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const updateState = useCallback((updates: Record<string, unknown>) => {
    updatePartData('sampleAnalysis', { ...partState, ...updates });
  }, [partState, updatePartData]);

  // Handle titration progress
  useEffect(() => {
    if (titrating && stirrerOn) {
      const interval = setInterval(() => {
        setTitrationProgress(prev => {
          if (prev >= 100) {
            setTitrating(false);
            return 100;
          }
          return prev + 2;
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [titrating, stirrerOn]);

  // Update flask color based on titration progress
  useEffect(() => {
    if (partState.indicatorAdded) {
      const colorFactor = titrationProgress / 100;
      const newColor = interpolateColor(EBT_WINE_RED, EBT_BLUE, colorFactor);
      updateState({ flaskColor: newColor });
    }
  }, [titrationProgress, partState.indicatorAdded]);

  const handlePipetteSample = useCallback(() => {
    updateState({ sampleAdded: true });
    completeStep('sampleAnalysis-1');
  }, [updateState, completeStep]);

  const handleAddBufferAndIndicator = useCallback(() => {
    if (partState.sampleAdded) {
      updateState({
        bufferAdded: true,
        indicatorAdded: true,
        flaskColor: EBT_WINE_RED
      });
      completeStep('sampleAnalysis-2');
    }
  }, [partState.sampleAdded, updateState, completeStep]);

  const handleRefillBurette = useCallback(() => {
    if (partState.indicatorAdded) {
      updateState({ buretteVolume: 50 });
      completeStep('sampleAnalysis-3');
    }
  }, [partState.indicatorAdded, updateState, completeStep]);

  const handleStartTitration = useCallback(() => {
    if (isStepCompleted('sampleAnalysis-3')) {
      setTitrating(true);
      setStirrerOn(true);
      updateState({ stopcockOpen: true });
    }
  }, [isStepCompleted, updateState]);

  const handleStopTitration = useCallback(() => {
    setTitrating(false);
    updateState({ stopcockOpen: false });

    // Calculate volume used - random between 15-30 mL for water sample
    const volumeUsed = (titrationProgress / 100) * 15 + 15;

    updateState({
      buretteVolume: 50 - volumeUsed,
      sampleResult: {
        ...partState.sampleResult,
        volumeEDTA: volumeUsed
      }
    });

    if (titrationProgress >= 95) {
      completeStep('sampleAnalysis-4');
    }
  }, [titrationProgress, partState.sampleResult, updateState, completeStep]);

  const handleRecordVolume = useCallback(() => {
    if (partState.sampleResult?.volumeEDTA) {
      completeStep('sampleAnalysis-5');
    }
  }, [partState.sampleResult, completeStep]);

  const getFlaskFillLevel = () => {
    if (partState.bufferAdded) return 45;
    if (partState.sampleAdded) return 35;
    return 0;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part III: Water Sample Analysis</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Obtain and Dilute Sample', hint: 'Dilute unknown 5-fold, pipette 25.00 mL into 3 flasks' },
            { id: 2, title: 'Add Buffer, Mg/EDTA, and Indicator', hint: 'Add 2 mL buffer, 1 mL Mg/EDTA, and 5-6 drops EBT' },
            { id: 3, title: 'Refill Burette', hint: 'Ensure burette is filled with EDTA' },
            { id: 4, title: 'Titrate Sample', hint: 'Add EDTA until red/purple disappears and distinct blue appears' },
            { id: 5, title: 'Record Volume', hint: 'Record volume of EDTA used' }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${isStepCompleted(`sampleAnalysis-${step.id}`) ? 'completed' : ''} ${
                state?.currentStep === step.id ? 'active' : ''
              }`}
            >
              <div className={`step-number ${isStepCompleted(`sampleAnalysis-${step.id}`) ? 'done' : ''}`}>
                {isStepCompleted(`sampleAnalysis-${step.id}`) ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className="step-title">{step.title}</span>
                  {stepInfoContent[step.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {state?.currentStep === step.id && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Sample Info */}
        <div className="sample-info">
          <h4>Water Sample</h4>
          <p>Unknown hardness sample from local water source.</p>
          {partState.sampleResult?.volumeEDTA && (
            <div className="result-display">
              EDTA Used: {partState.sampleResult.volumeEDTA.toFixed(2)} mL
            </div>
          )}
        </div>
      </div>

      <LabBench
        experimentName="Water Sample Analysis"
        completedCount={state?.completedSteps.filter(s => s.startsWith('sampleAnalysis-')).length || 0}
        totalSteps={5}
        equipment={['Burette', 'Erlenmeyer flask', 'Pipette']}
        labMode="guided"
        currentStep={state?.currentStep || 1}
      >
        <div className="sample-analysis-workspace">
          <div className="titration-setup">
            {/* Burette */}
            <div className="burette-station">
              <Burette
                volume={partState.buretteVolume}
                maxVolume={50}
                liquidColor="#7DD3FC"
                stopcock={partState.stopcockOpen ? 'open' : 'closed'}
                onStopcock={() => {
                  if (titrating) {
                    handleStopTitration();
                  } else if (isStepCompleted('sampleAnalysis-3') && !isStepCompleted('sampleAnalysis-4')) {
                    handleStartTitration();
                  }
                }}
              />
              <span className="equipment-label">EDTA Solution</span>
            </div>

            {/* Flask on Stirrer */}
            <div className="flask-assembly">
              <ErlenmeyerFlask
                size={250}
                fillLevel={getFlaskFillLevel()}
                fillColor={partState.flaskColor}
                hasStirBar={true}
                stirring={stirrerOn}
                label="Water Sample"
                active={
                  !isStepCompleted('sampleAnalysis-1') ||
                  (!isStepCompleted('sampleAnalysis-2') && partState.sampleAdded)
                }
                onClick={() => {
                  if (!isStepCompleted('sampleAnalysis-1')) {
                    handlePipetteSample();
                  } else if (!isStepCompleted('sampleAnalysis-2') && partState.sampleAdded) {
                    handleAddBufferAndIndicator();
                  }
                }}
              />
              <MagneticStirrer
                speed={5}
                isOn={stirrerOn}
                hasFlask={true}
                onClick={() => setStirrerOn(!stirrerOn)}
              />
            </div>

            {/* Water Sample Beaker */}
            <div className="sample-beaker">
              <BeakerIcon size={60} color="#7DD3FC" />
              <span className="equipment-label">Water Sample</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {!isStepCompleted('sampleAnalysis-3') && partState.indicatorAdded && (
              <button className="action-btn primary" onClick={handleRefillBurette}>
                <Droplets size={18} />
                Refill Burette
              </button>
            )}

            {isStepCompleted('sampleAnalysis-3') && !isStepCompleted('sampleAnalysis-4') && (
              <div className="titration-controls">
                <button
                  className={`action-btn ${titrating ? 'danger' : 'success'}`}
                  onClick={titrating ? handleStopTitration : handleStartTitration}
                >
                  {titrating ? 'Stop at Endpoint' : 'Begin Titration'}
                </button>
                <div className="titration-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${titrationProgress}%`,
                        backgroundColor: partState.flaskColor
                      }}
                    />
                  </div>
                  <span>{titrationProgress.toFixed(0)}%</span>
                </div>
                <p className="hint-text">Watch for color change from red to distinct blue</p>
              </div>
            )}

            {!isStepCompleted('sampleAnalysis-5') && isStepCompleted('sampleAnalysis-4') && (
              <button className="action-btn primary" onClick={handleRecordVolume}>
                <Check size={18} />
                Record Volume: {partState.sampleResult?.volumeEDTA?.toFixed(2)} mL
              </button>
            )}

            {isStepCompleted('sampleAnalysis-5') && (
              <div className="completion-message">
                <Check size={24} />
                <span>Sample analysis complete! Proceed to calculations.</span>
              </div>
            )}
          </div>

          {/* Color Legend */}
          <div className="color-legend">
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: EBT_WINE_RED }} />
              <span>Before endpoint</span>
            </div>
            <div className="legend-item">
              <div className="color-swatch" style={{ backgroundColor: EBT_BLUE }} />
              <span>At endpoint</span>
            </div>
          </div>
        </div>
      </LabBench>

      <style>{`
        .sample-analysis-workspace {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 20px;
          width: 100%;
        }

        .titration-setup {
          display: flex;
          gap: 50px;
          align-items: flex-end;
        }

        .burette-station, .flask-assembly, .sample-beaker {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .equipment-label {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .sample-info {
          margin-top: 20px;
          padding: 12px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .sample-info h4 {
          font-size: 12px;
          color: var(--text-muted);
          margin: 0 0 8px 0;
          text-transform: uppercase;
        }

        .sample-info p {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0 0 8px 0;
        }

        .result-display {
          background: var(--bg-dark);
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--secondary);
          font-family: 'Courier New', monospace;
        }

        .action-buttons {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .action-btn.primary {
          background: var(--primary);
          color: white;
        }

        .action-btn.success {
          background: var(--secondary);
          color: white;
        }

        .action-btn.danger {
          background: var(--danger);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
        }

        .titration-controls {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .titration-progress {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 200px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: var(--bg-dark);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          transition: width 0.2s, background-color 0.3s;
        }

        .hint-text {
          font-size: 12px;
          color: var(--text-muted);
          font-style: italic;
        }

        .completion-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 16px 24px;
          background: rgba(0, 168, 107, 0.2);
          border-radius: 8px;
          color: var(--secondary);
          font-weight: 500;
        }

        .color-legend {
          display: flex;
          gap: 24px;
          padding: 12px 20px;
          background: var(--bg-elevated);
          border-radius: 8px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .color-swatch {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          border: 1px solid rgba(255,255,255,0.2);
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
      `}</style>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp4"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
